import {
  Box,
  ButtonGroup,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
} from "@material-ui/core";
import keyBy from "lodash/keyBy";
import * as React from "react";
import { useState } from "react";
import { Datagrid, FunctionField, ListContextProvider, useNotify } from "react-admin";
import { useAppDataProvider } from "../../dataprovider";
import { CustomDateField } from "../CustomDateField";
import { EnumField } from "../EnumField";
import CreatedByField from "../ReferenceFields/CreatedBy/CreatedByField";
import RemoveCircleOutline from "@material-ui/icons/RemoveCircleOutline";
import { Skeleton } from "@material-ui/lab";
import { GetPbhRequestServiceableFileType, PbhRequestFileType } from "../../../src/Enums/PbhRequestFileType";
import { DownloadButton, useAuth } from "@airborne/react-admin";
import { connect } from "react-redux";
import { PbhRequestOrderTabEnum } from "../../Enums/PbhRequestOrderTabEnum";
import { getEnumDescription } from "../../Helper/EnumHelper";
import { GetCoreInfoRequestReasonTypeEnum } from "../../Enums/CoreInfoRequestReasonTypeEnum";

const PbhFileGrid = props => {
  const [fileList, setFileList] = useState([] as any);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState<any>(null);
  const [validationError, setValidationError] = useState<string>("");
  const { user } = useAuth();
  const notify = useNotify();
  const provider = useAppDataProvider();

  React.useEffect(() => {
    if (props?.files !== undefined) {
      var activeTabData = props?.files?.filter?.(x => {
        // Core Info Request dosyaları için coreInfoRequestFileType'a göre filtreleme yap
        if (x.coreInfoRequestId) {
          // coreInfoRequestFileType'ı pbhRequestFileTypeGroupEnum gibi ele al
          const targetGroup =
            props?.activeTab == PbhRequestOrderTabEnum.InfoTab ? PbhRequestFileType.Info : props?.activeTab;

          return true; // Tüm Core Info dosyalarını göster, backend zaten filtrelenmiş gönderiyor
        }

        // Normal PBH Request dosyaları için mevcut filtreleme mantığı
        return (
          x.pbhRequestFileTypeGroupEnum ==
          (props?.activeTab == PbhRequestOrderTabEnum.InfoTab ? PbhRequestFileType.Info : props?.activeTab)
        );
      });
      setFileList(activeTabData);
    }
  }, [props?.files, props?.activeTab]);

  const validateDeletion = recordToDelete => {
    if (!recordToDelete) return { canDelete: true, message: "" };

    // Core Info Request dosyaları silinemez
    if (recordToDelete.coreInfoRequestId) {
      return { canDelete: false, message: "Core Info Request files cannot be deleted from here." };
    }

    const recordType = recordToDelete.pbhRequestFileTypeEnum;

    // Different validation rules based on active tab
    if (props?.activeTab == PbhRequestOrderTabEnum.ServiceableTab) {
      // ServiceableTab: Must have either 1 "FORM ONE & SHOP REPORT" OR (1 "FORM ONE" AND 1 "SHOP REPORT" separately)
      if (
        recordType === Number(PbhRequestFileType.FormOne) ||
        recordType === Number(PbhRequestFileType.ShopReport) ||
        recordType === Number(PbhRequestFileType.FormOneShopReport)
      ) {
        // Count remaining documents after deletion
        const remainingFiles = fileList.filter(file => file.id !== recordToDelete.id);

        const remainingFormOneCount = remainingFiles.filter(
          file => file.pbhRequestFileTypeEnum === Number(PbhRequestFileType.FormOne)
        ).length;

        const remainingShopReportCount = remainingFiles.filter(
          file => file.pbhRequestFileTypeEnum === Number(PbhRequestFileType.ShopReport)
        ).length;

        const remainingFormOneShopReportCount = remainingFiles.filter(
          file => file.pbhRequestFileTypeEnum === Number(PbhRequestFileType.FormOneShopReport)
        ).length;

        // Check if after deletion we still meet the requirement:
        // Either at least 1 "FORM ONE & SHOP REPORT" OR (at least 1 "FORM ONE" AND 1 "SHOP REPORT")
        const hasFormOneShopReport = remainingFormOneShopReportCount > 0;
        const hasBothSeparately = remainingFormOneCount > 0 && remainingShopReportCount > 0;

        if (!hasFormOneShopReport && !hasBothSeparately) {
          return {
            canDelete: false,
            message: "You cannot delete this document without uploading the second document of the required file type.",
          };
        }
      }
    } else if (props?.activeTab == PbhRequestOrderTabEnum.InfoTab) {
      // InfoTab: Check if we're trying to delete Mailings and it's the last one
      if (recordType === Number(PbhRequestFileType.Mailings)) {
        // Count existing Mailings documents (excluding the one being deleted)
        const remainingMailings = fileList.filter(
          file => file.id !== recordToDelete.id && file.pbhRequestFileTypeEnum === Number(PbhRequestFileType.Mailings)
        ).length;

        if (remainingMailings === 0) {
          return {
            canDelete: false,
            message: "You cannot delete this document without uploading the second document of the required file type.",
          };
        }
      }
    } else if (props?.activeTab == PbhRequestOrderTabEnum.UnserviceableTab) {
      // UnserviceableTab: Check if we're trying to delete US-TAG and it's the last one
      if (recordType === Number(PbhRequestFileType.UsTag)) {
        // Count existing US-TAG documents (excluding the one being deleted)
        const remainingUsTags = fileList.filter(
          file => file.id !== recordToDelete.id && file.pbhRequestFileTypeEnum === Number(PbhRequestFileType.UsTag)
        ).length;

        if (remainingUsTags === 0) {
          return {
            canDelete: false,
            message: "You cannot delete this document without uploading the second document of the required file type.",
          };
        }
      }

      if (recordType === Number(PbhRequestFileType.UnusedStatement)) {
        // Count existing US-TAG documents (excluding the one being deleted)
        const remainingUnusedStatement = fileList.filter(
          file =>
            file.id !== recordToDelete.id && file.pbhRequestFileTypeEnum === Number(PbhRequestFileType.UnusedStatement)
        ).length;

        if (remainingUnusedStatement === 0) {
          return {
            canDelete: false,
            message: "You cannot delete this document without uploading the second document of the required file type.",
          };
        }
      }
    }

    return { canDelete: true, message: "" };
  };

  const removeButtonClick = record => {
    // Core Info Request dosyalarını silme işlemine izin verme
    if (record.coreInfoRequestId) {
      notify("Core Info Request files cannot be deleted from here", "warning", false, true);
      return;
    }

    setRecordToDelete(record);
    const validation = validateDeletion(record);
    setValidationError(validation.canDelete ? "" : validation.message);
    setConfirmDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (recordToDelete && !validationError) {
      await provider
        .deleteFileRelation(recordToDelete.id, props.source)
        .then(result => {
          notify(`Record Deleted`, "success", false, true);
          props.fileAction();
        })
        .catch(error => {
          notify(`Error : ${error}`, "error", false, true);
        })
        .finally(() => {
          setConfirmDialogOpen(false);
          setRecordToDelete(null);
          setValidationError("");
        });
    }
  };

  const handleCancelDelete = () => {
    setConfirmDialogOpen(false);
    setRecordToDelete(null);
    setValidationError("");
  };

  return (
    <div>
      {props.filesLoading ? (
        <Box p={2}>
          <Skeleton variant="rect" height={200} style={{ marginBottom: 8, borderRadius: 4 }} />
        </Box>
      ) : (
        <ListContextProvider
          value={{
            data: keyBy(fileList, "id"),
            ids: fileList.map(({ id }) => id),
            currentSort: { field: "id", order: "DESC" },
          }}
        >
          <Datagrid {...props}>
            {props.canRemove &&
              (!user?.isCustomer ||
                (user.isCustomer && props?.activeTab == PbhRequestOrderTabEnum.UnserviceableTab)) && (
                <FunctionField
                  render={(record: any) => (
                    <Box style={{ flex: 1, flexDirection: "row" }}>
                      {!record.coreInfoRequestId && (
                        <ButtonGroup variant="text" size="small">
                          <IconButton
                            onClick={e => {
                              e?.stopPropagation?.();
                              removeButtonClick(record);
                            }}
                            color="primary"
                          >
                            <RemoveCircleOutline />
                          </IconButton>
                        </ButtonGroup>
                      )}
                    </Box>
                  )}
                />
              )}
            <FunctionField
              style={{ fontSize: 12 }}
              label="Name"
              render={(record: any) => (
                <DownloadButton target={`download/${record.objectKey}`} buttonText={record.name} variant="text" />
              )}
            />
            {props?.activeTab != PbhRequestOrderTabEnum.UnserviceableTab && (
              <FunctionField
                style={{ fontSize: 12 }}
                label="File Type"
                render={(record: any) => {
                  return getEnumDescription(record.pbhRequestFileTypeEnum, GetPbhRequestServiceableFileType);
                }}
              />
            )}
            {props?.activeTab == PbhRequestOrderTabEnum.UnserviceableTab && (
              <FunctionField
                style={{ fontSize: 12 }}
                label="Core Unit File Type"
                render={(record: any) => {
                  // Normal PBH Request dosyası için göster
                  if (!record.coreInfoRequestId) {
                    return getEnumDescription(record.pbhRequestFileTypeEnum, GetPbhRequestServiceableFileType);
                  }
                  return;
                }}
              />
            )}
            {props?.activeTab == PbhRequestOrderTabEnum.UnserviceableTab && (
              <FunctionField
                style={{ fontSize: 12 }}
                label="Core Info Request Type"
                render={(record: any) => {
                  // Core Info Request dosyası için göster
                  if (record.coreInfoRequestId) {
                    return getEnumDescription(record.coreInfoRequestFileType, GetCoreInfoRequestReasonTypeEnum);
                  }
                  return;
                }}
              />
            )}
            {props?.showGroup && (
              <EnumField label={"Group Type"} enumName={"PbhRequestFileType"} source={"pbhRequestFileTypeGroupEnum"} />
            )}
            <CustomDateField source="createdDate" label="CREATED DATE" />
            <CreatedByField label="CREATED BY" link={false} />
          </Datagrid>
        </ListContextProvider>
      )}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelDelete}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {validationError ? "Cannot Delete Document" : "Confirm Delete"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {validationError ? validationError : `Are you sure you want to delete document "${recordToDelete?.name}"?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            {validationError ? "Yes" : "Cancel"}
          </Button>
          {!validationError && (
            <Button onClick={handleConfirmDelete} color="primary" autoFocus>
              Yes
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </div>
  );
};

const mapStateToProps = state => ({
  files: state?.pbhFiles?.files ?? [],
  filesLoading: state?.pbhFiles?.filesLoading ?? false,
  isPbhRequestInfoUpdate: state?.pbhRequestInfoUpdate?.isUpdate ?? false,
});
const mapDispatchToProps = dispatch => ({});
export default connect(mapStateToProps, mapDispatchToProps)(PbhFileGrid);
